<template>
  <div class="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
    <TopNav @toggle-sidebar="toggleSidebar" />
    <SideMenu :isOpen="isSidebarOpen" @close-sidebar="isSidebarOpen = false" />

    <main class="p-6 md:ml-64 h-auto pt-20 flex justify-center">
      <section class="w-full max-w-lg dark:bg-gray-800 rounded-lg p-8">
        <div class="mb-6 flex justify-between items-center">
          <h2 class="text-3xl font-semibold text-gray-800 dark:text-gray-100">Edit LMS Instance</h2>
          <router-link :to="$routeWithBranch('ViewInstance', { params: { id: instance.id } })"
            class="mt-4 ml-4 flex items-center text-primary-700 dark:text-primary-300 focus:outline-none">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span class="ml-2">Back</span>
          </router-link>
        </div>

        <div v-if="loading" class="text-center py-10">
          <svg aria-hidden="true"
            class="w-14 h-14 mx-auto text-gray-200 animate-spin dark:text-gray-600 fill-secondary-600"
            viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor" />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill" />
          </svg>
          <span class="sr-only">Loading...</span>
        </div>

        <form v-if="!loading" @submit.prevent="updateInstance" enctype="multipart/form-data">
          <div v-if="errors.length" class="mb-4 bg-red-100 text-red-800 p-3 rounded">
            <ul>
              <li v-for="(error, index) in errors" :key="index" class="text-sm">{{ error }}</li>
            </ul>
          </div>

          <div class="mb-4">
            <label for="name" class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Instance Name</label>
            <input type="text" id="name" v-model="instance.name"
              class="w-full p-3 border rounded-lg text-gray-800 dark:text-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500"
              required />
          </div>

          <div class="mb-4">
            <label for="domain" class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Domain</label>
            <input type="text" id="domain" v-model="instance.domain"
              class="w-full p-3 border rounded-lg text-gray-800 dark:text-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500" />
          </div>

          <div class="mb-4">
            <label for="license_count" class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">License Count</label>
            <input type="number" id="license_count" v-model.number="instance.license_count"
              class="w-full p-3 border rounded-lg text-gray-800 dark:text-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500" />
          </div>

          <div class="mb-4">
            <label for="branch_limit" class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Branch Limit</label>
            <input type="number" id="branch_limit" v-model.number="instance.branch_limit"
              class="w-full p-3 border rounded-lg text-gray-800 dark:text-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-primary-500" />
          </div>

          <!-- Logo Upload -->
          <div class="mb-4">
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-400 mb-1">Instance Logo</label>
            <input type="file" @change="handleLogoUpload" accept="image/*" />

            <div v-if="logoPreview || instance.logo_url" class="relative mt-2 w-32 h-32">
              <img :src="logoPreview || logoUrl" alt="Instance Logo"
                class="w-full h-full object-cover rounded-md shadow-md" />
              <button @click.prevent="removeLogo"
                class="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 flex items-center justify-center shadow-lg hover:bg-red-700 transition">
                ✖
              </button>
            </div>
          </div>

          <div class="mb-4">
            <label class="flex items-center space-x-2">
              <input type="checkbox" v-model="instance.api_access"
                class="rounded border-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:ring-primary-500" />
              <span class="text-sm text-gray-700 dark:text-gray-300">API Access Enabled</span>
            </label>
          </div>

          <div class="mb-4">
            <label class="flex items-center space-x-2">
              <input type="checkbox" v-model="instance.branding_enabled"
                class="rounded border-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:ring-primary-500" />
              <span class="text-sm text-gray-700 dark:text-gray-300">Branding Enabled</span>
            </label>
          </div>

          <div class="mb-4">
            <label class="flex items-center space-x-2">
              <input type="checkbox" v-model="instance.is_active"
                class="rounded border-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:ring-primary-500" />
              <span class="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>
          </div>

          <div class="mt-6">
            <button type="submit" :disabled="isSubmitting"
              class="w-full !bg-secondary-600 text-white font-semibold py-3 rounded-lg hover:!bg-secondary-700 flex items-center justify-center">
              <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
              </svg>
              {{ isSubmitting ? 'Updating...' : 'Update Instance' }}
            </button>
          </div>
        </form>
      </section>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import TopNav from '@/components/headers/TopNav.vue';
import SideMenu from '@/components/SideMenu.vue';

export default {
  components: { TopNav, SideMenu },
  data() {
    return {
      instance: {
        name: '',
        domain: '',
        license_count: null,
        branch_limit: null,
        api_access: false,
        branding_enabled: false,
        is_active: true,
        logo_url: null,
      },
      logoFile: null,
      logoPreview: null,
      loading: true,
      isSubmitting: false,
      errors: [],
      isSidebarOpen: false,
    };
  },
  computed: {
    logoUrl() {
      return this.instance.logo_url ? `${process.env.VUE_APP_API_URL}${this.instance.logo_url}` : null;
    },
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    async fetchInstance() {
      this.loading = true;
      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, {
          query: `
            query($id: Int!) {
              lmsInstance(id: $id) {
                id
                name
                domain
                license_count
                branch_limit
                api_access
                branding_enabled
                is_active
                logo_url
              }
            }
          `,
          variables: { id: parseInt(this.$route.params.id) },
        });
        this.instance = response.data.data.lmsInstance;
      } catch (error) {
        console.error("Error fetching instance:", error);
        this.errors.push("Unable to fetch instance details.");
      } finally {
        this.loading = false;
      }
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.logoFile = file;
        this.logoPreview = URL.createObjectURL(file);
      }
    },
    removeLogo() {
      this.logoFile = null;
      this.logoPreview = null;
      this.instance.logo_url = null; // clear existing logo
    },
    async updateInstance() {
      this.isSubmitting = true;
      this.errors = [];

      const variables = {
        id: parseInt(this.$route.params.id),
        name: this.instance.name,
        domain: this.instance.domain,
        license_count: this.instance.license_count,
        branch_limit: this.instance.branch_limit,
        api_access: this.instance.api_access,
        branding_enabled: this.instance.branding_enabled,
        is_active: this.instance.is_active,
      };

      const formData = new FormData();

      if (this.logoFile) {
        variables.logo = null; // placeholder for file
        formData.append('map', JSON.stringify({ '1': ['variables.logo'] }));
        formData.append('1', this.logoFile);
      } else if (this.instance.logo_url === null) {
        // Explicitly removed logo
        variables.logo = null;
        formData.append('map', '{}');
      } else {
        // Logo unchanged
        formData.append('map', '{}');
      }

      formData.append('operations', JSON.stringify({
        query: `
          mutation UpdateLmsInstance($id: Int!, $name: String, $domain: String, $license_count: Int, $branch_limit: Int, $api_access: Boolean, $branding_enabled: Boolean, $is_active: Boolean, $logo: Upload) {
            updateLmsInstance(
              id: $id,
              name: $name,
              domain: $domain,
              license_count: $license_count,
              branch_limit: $branch_limit,
              api_access: $api_access,
              branding_enabled: $branding_enabled,
              is_active: $is_active,
              logo: $logo
            ) {
              id
              name
              logo_url
            }
          }
        `,
        variables,
      }));

      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/graphql`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.data.updateLmsInstance) {
          this.$routerPushWithBranch('ViewInstance', { params: { id: this.$route.params.id } });
        }
      } catch (error) {
        console.error("Error updating instance:", error);
        this.errors.push("An error occurred while updating the instance.");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
  created() {
    this.fetchInstance();
  },
};
</script>
